import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCar,
  faFire,
  faImage,
  faComment,
} from "@fortawesome/free-solid-svg-icons";

const SpotterAvatar = ({
  username,
  profilePic,
  profilePicVariants,
  size = 24,
}) => {
  const [imgError, setImgError] = useState(false);
  const src = profilePicVariants?.small || profilePic || null;
  if (!src || imgError) {
    return (
      <div
        className={`flex items-center justify-center bg-blue-50 rounded-full dark:bg-blue-900 dark:text-blue-100`}
        style={{ width: size, height: size }}
      >
        <span
          className="text-blue-300 dark:text-blue-100"
          style={{ fontSize: size * 0.6 }}
        >
          {username.charAt(0).toUpperCase()}
        </span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={username}
      className="object-cover rounded-full bg-gray-100 dark:bg-gray-800"
      style={{ width: size, height: size }}
      onError={() => setImgError(true)}
    />
  );
};

const SpotterRow = ({
  rank,
  username,
  platesFound,
  profilePic,
  profilePicVariants,
  spotterStats,
  podium = false,
}) => {
  const isTopThree = rank <= 3;

  if (podium) {
    // Podium style: vertical, centered, larger avatar, bolder username, no rank/medal in avatar
    return (
      <Link
        to={`/u/${username}`}
        className={`flex flex-col items-center justify-end w-full h-full rounded-t-lg shadow border border-gray-100 p-2 transition-all duration-200 dark:border-gray-700 dark:bg-gray-800 ${
          rank === 1
            ? "bg-yellow-50 dark:bg-yellow-900"
            : rank === 2
            ? "bg-gray-50 dark:bg-gray-900"
            : rank === 3
            ? "bg-orange-50 dark:bg-orange-900"
            : "bg-white dark:bg-gray-900"
        }`}
        style={{ minHeight: 0 }}
      >
        <div className={`mb-1 relative ${rank === 1 ? "bottom-4" : ""}`}>
          <div
            className={`absolute inset-0 rounded-full ring-2 ${
              rank === 1
                ? "ring-yellow-400 shadow-[0_0_15px_rgba(234,179,8,0.5)]"
                : rank === 2
                ? "ring-gray-300 shadow-[0_0_15px_rgba(209,213,219,0.5)]"
                : "ring-orange-400 shadow-[0_0_15px_rgba(251,146,60,0.5)]"
            }`}
          />
          <SpotterAvatar
            username={username}
            profilePic={profilePic}
            profilePicVariants={profilePicVariants}
            size={rank === 1 ? 64 : rank === 2 ? 56 : 48}
          />
        </div>
        <div className="flex flex-wrap justify-center gap-2 text-xs text-gray-600 dark:text-gray-400">
          <span className="flex items-center">
            <FontAwesomeIcon icon={faCar} className="mr-1" />
            {platesFound}
          </span>
          {spotterStats?.currentStreak?.days > 0 && (
            <span className="flex items-center text-orange-500 animate-pulse dark:text-orange-400">
              <FontAwesomeIcon icon={faFire} className="mr-1" />
              {spotterStats.currentStreak.days}
            </span>
          )}
          {spotterStats?.totalImages > 0 && (
            <span className="flex items-center text-blue-500 dark:text-blue-400">
              <FontAwesomeIcon icon={faImage} className="mr-1" />
              {spotterStats.totalImages}
            </span>
          )}
          {spotterStats?.totalComments > 0 && (
            <span className="flex items-center text-green-500 dark:text-green-400">
              <FontAwesomeIcon icon={faComment} className="mr-1" />
              {spotterStats.totalComments}
            </span>
          )}
        </div>
      </Link>
    );
  }

  // Default (list) style
  return (
    <Link
      to={`/u/${username}`}
      className="flex items-center p-2 hover:bg-gray-50 rounded my-1 transition-all duration-200 dark:hover:bg-gray-700 dark:bg-gray-800"
    >
      <SpotterAvatar
        username={username}
        profilePic={profilePic}
        profilePicVariants={profilePicVariants}
        size={32}
      />
      <div className="ml-3 flex flex-col items-start">
        <span
          className={`font-medium ${isTopThree ? "animate-pulse" : ""}`}
          style={{
            color: `hsl(${0 + (rank - 8) * 4}, 100%, 50%)`,
          }}
        >
          {username}
        </span>
        <span className="text-gray-400 text-xs mt-0.5 text-left dark:text-gray-400">
          <FontAwesomeIcon icon={faCar} className="mr-1" />
          {platesFound}
          {spotterStats?.currentStreak?.days > 0 && (
            <span className="ml-2 text-orange-500 animate-pulse">
              <FontAwesomeIcon icon={faFire} className="mr-1" />
              {spotterStats.currentStreak.days}
            </span>
          )}
          {spotterStats?.totalImages > 0 && (
            <span className="ml-2 text-blue-500 dark:text-blue-400">
              <FontAwesomeIcon icon={faImage} className="mr-1" />
              {spotterStats.totalImages}
            </span>
          )}
          {spotterStats?.totalComments > 0 && (
            <span className="ml-2 text-green-500 dark:text-green-400">
              <FontAwesomeIcon icon={faComment} className="mr-1" />
              {spotterStats.totalComments}
            </span>
          )}
        </span>
      </div>
    </Link>
  );
};

export default React.memo(SpotterRow);
